import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { LoadingImage } from "@/components/LoadingImage";
import { SEO } from "@/components/SEO";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  MagnifyingGlass, Buildings, User, ChartLineUp, CalendarCheck, MapPin,
  ChatCircle, SquaresFour, ListBullets, Shield, CaretDown
} from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "president", label: "President" },
  { value: "vice_president", label: "Vice President" },
  { value: "secretary", label: "Secretary" },
];

const ROLE_COLORS = {
  super_admin: "bg-red-500/10 text-red-500",
  president: "bg-amber-500/10 text-amber-600",
  vice_president: "bg-violet-500/10 text-violet-600",
  secretary: "bg-sky-500/10 text-sky-600",
  member: "bg-emerald-500/10 text-emerald-600",
};

export default function MembersPage() {
  const { token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [layout, setLayout] = useState("card");
  const isSuperAdmin = user?.role === "super_admin";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const cityFilter = searchParams.get("city") || "all";
  const chapterFilter = searchParams.get("chapter") || "all";
  const categoryFilter = searchParams.get("category") || "all";

  const updateFilter = (key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === "all") next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  };

  const fetchMembers = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      if (chapterFilter !== "all") params.append("chapter_id", chapterFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      const res = await axios.get(`${API}/members?${params}`, { headers });
      setMembers(res.data);
    } catch (error) {
      console.error("Failed to fetch members:", error);
    } finally {
      setLoading(false);
    }
  }, [token, chapterFilter, categoryFilter]);

  const fetchInitial = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [chRes, catRes, cityRes] = await Promise.all([
        axios.get(`${API}/chapters`, { headers }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/chapters/cities`, { headers })
      ]);
      setChapters(chRes.data);
      setCategories(catRes.data);
      setCities(cityRes.data.map(c => c.city).filter(Boolean));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  usePolling(fetchMembers, 15000);

  const changeRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${memberId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Role updated successfully");
      fetchMembers();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to update role");
    }
  };

  const filteredChapters = cityFilter === "all" ? chapters : chapters.filter(ch => ch.city === cityFilter);

  const filteredMembers = members.filter(m =>
    m.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <SEO title="Members Directory" description="Browse and connect with verified BCH members across chapters and categories." path="/members" noIndex />
      <div className="space-y-6" data-testid="members-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-lg">
                <User className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />
              </div>
              Members Directory
            </h1>
            <p className="text-muted text-sm mt-1 ml-[52px] md:ml-[60px]">{filteredMembers.length} members</p>
          </div>
          {/* Layout Toggle */}
          <div className="flex items-center gap-1 bg-card border border-[var(--border-color)] rounded-xl p-1" data-testid="layout-toggle">
            <button
              onClick={() => setLayout("card")}
              className={`p-2 rounded-lg transition-all ${layout === "card" ? "bg-bch-teal text-white shadow-sm" : "text-muted hover:text-primary"}`}
              data-testid="layout-card-btn"
            >
              <SquaresFour className="w-4 h-4" weight={layout === "card" ? "fill" : "duotone"} />
            </button>
            <button
              onClick={() => setLayout("list")}
              className={`p-2 rounded-lg transition-all ${layout === "list" ? "bg-bch-teal text-white shadow-sm" : "text-muted hover:text-primary"}`}
              data-testid="layout-list-btn"
            >
              <ListBullets className="w-4 h-4" weight={layout === "list" ? "fill" : "duotone"} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" weight="duotone" />
            <Input
              placeholder="Search by name or business..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="members-search"
              className="pl-9 bg-card border-[var(--border-color)] text-primary"
            />
          </div>
          <Select value={cityFilter} onValueChange={(v) => { updateFilter("city", v); updateFilter("chapter", "all"); }}>
            <SelectTrigger className="w-[150px] bg-card border-[var(--border-color)] text-primary" data-testid="city-filter">
              <MapPin className="w-4 h-4 text-rose-400 mr-1" weight="fill" />
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent className="bg-card border-[var(--border-color)]">
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={chapterFilter} onValueChange={v => updateFilter("chapter", v)}>
            <SelectTrigger className="w-[160px] bg-card border-[var(--border-color)] text-primary" data-testid="chapter-filter">
              <Buildings className="w-4 h-4 text-bch-teal mr-1" weight="duotone" />
              <SelectValue placeholder="Chapter" />
            </SelectTrigger>
            <SelectContent className="bg-card border-[var(--border-color)]">
              <SelectItem value="all">All Chapters</SelectItem>
              {filteredChapters.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={v => updateFilter("category", v)}>
            <SelectTrigger className="w-[160px] bg-card border-[var(--border-color)] text-primary" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-card border-[var(--border-color)]">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Members Grid / List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : layout === "card" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-card-grid">
            {filteredMembers.map(m => (
              <MemberCard key={m.id} member={m} isSuperAdmin={isSuperAdmin} onChangeRole={changeRole} navigate={navigate} />
            ))}
          </div>
        ) : (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden" data-testid="members-list">
            <div className="grid grid-cols-[2fr_2fr_1.5fr_140px_80px] gap-4 px-5 py-3 border-b border-[var(--border-color)] text-xs font-semibold text-muted uppercase tracking-wider">
              <span>Member</span>
              <span>Business</span>
              <span>Chapter</span>
              <span className="text-center">Role</span>
              <span className="text-center">Actions</span>
            </div>
            {filteredMembers.map(m => (
              <MemberRow key={m.id} member={m} isSuperAdmin={isSuperAdmin} onChangeRole={changeRole} navigate={navigate} />
            ))}
          </div>
        )}

        {!loading && filteredMembers.length === 0 && (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl p-12 text-center">
            <User className="w-16 h-16 text-muted/20 mx-auto mb-4" weight="duotone" />
            <p className="text-primary font-semibold text-lg">No members found</p>
            <p className="text-muted text-sm mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </>
  );
}

const MemberCard = ({ member, isSuperAdmin, onChangeRole, navigate }) => {
  const roleColor = ROLE_COLORS[member.role] || ROLE_COLORS.member;
  return (
    <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`member-card-${member.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <Link to={`/p/${member.id}`} className="shrink-0">
          {member.profile_picture ? (
            <LoadingImage src={member.profile_picture} className="w-12 h-12 rounded-xl" fallback={
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                <span className="text-white text-lg font-bold">{member.full_name?.charAt(0)}</span>
              </div>
            } />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
              <span className="text-white text-lg font-bold">{member.full_name?.charAt(0)}</span>
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/p/${member.id}`} className="text-primary font-semibold text-sm hover:text-bch-teal transition-colors truncate block">
            {member.full_name}
          </Link>
          <p className="text-muted text-xs truncate">{member.business_name || "—"}</p>
          <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${roleColor}`}>
            {member.role?.replace("_", " ")}
          </span>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-xs text-muted">
          <Buildings className="w-3.5 h-3.5 text-bch-teal" weight="duotone" />
          <span className="truncate">{member.chapter_name || "—"}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <ChartLineUp className="w-3.5 h-3.5 text-amber-500" weight="duotone" />
          <span>{member.business_category || "—"}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-[var(--border-color)]">
        <button
          onClick={() => navigate(`/chat?dm=${member.id}&name=${encodeURIComponent(member.full_name)}`)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium text-bch-teal bg-bch-teal/10 hover:bg-bch-teal/20 transition-colors"
          data-testid={`chat-btn-${member.id}`}
        >
          <ChatCircle className="w-3.5 h-3.5" weight="fill" /> Chat
        </button>
        <Link
          to={`/p/${member.id}`}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium text-violet-500 bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
          data-testid={`profile-btn-${member.id}`}
        >
          <User className="w-3.5 h-3.5" weight="fill" /> Profile
        </Link>
      </div>

      {isSuperAdmin && member.role !== "super_admin" && (
        <div className="mt-3 pt-3 border-t border-[var(--border-color)]">
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-muted" weight="duotone" />
            <span className="text-muted text-[10px] uppercase tracking-wider font-semibold">Change Role</span>
          </div>
          <select
            value={member.role}
            onChange={(e) => onChangeRole(member.id, e.target.value)}
            className="mt-1.5 w-full bg-surface border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-primary text-xs focus:outline-none focus:ring-2 focus:ring-bch-teal/30 cursor-pointer"
            data-testid={`role-select-${member.id}`}
          >
            {ROLE_OPTIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

const MemberRow = ({ member, isSuperAdmin, onChangeRole, navigate }) => {
  const roleColor = ROLE_COLORS[member.role] || ROLE_COLORS.member;
  return (
    <div className="grid grid-cols-[2fr_2fr_1.5fr_140px_80px] gap-4 px-5 py-3.5 items-center border-b border-[var(--border-color)] last:border-0 hover:bg-surface transition-colors" data-testid={`member-row-${member.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Link to={`/p/${member.id}`} className="shrink-0">
          {member.profile_picture ? (
            <LoadingImage src={member.profile_picture} className="w-9 h-9 rounded-lg" fallback={
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                <span className="text-white text-sm font-bold">{member.full_name?.charAt(0)}</span>
              </div>
            } />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
              <span className="text-white text-sm font-bold">{member.full_name?.charAt(0)}</span>
            </div>
          )}
        </Link>
        <Link to={`/p/${member.id}`} className="text-primary text-sm font-medium hover:text-bch-teal transition-colors truncate">
          {member.full_name}
        </Link>
      </div>
      <div className="min-w-0">
        <p className="text-primary text-sm truncate">{member.business_name || "—"}</p>
        <p className="text-muted text-xs truncate">{member.business_category || ""}</p>
      </div>
      <p className="text-muted text-sm truncate">{member.chapter_name || "—"}</p>
      <div className="text-center">
        {isSuperAdmin && member.role !== "super_admin" ? (
          <select
            value={member.role}
            onChange={(e) => onChangeRole(member.id, e.target.value)}
            className="bg-surface border border-[var(--border-color)] rounded-lg px-2 py-1 text-primary text-xs focus:outline-none focus:ring-2 focus:ring-bch-teal/30 cursor-pointer"
            data-testid={`role-select-row-${member.id}`}
          >
            {ROLE_OPTIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        ) : (
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${roleColor}`}>
            {member.role?.replace("_", " ")}
          </span>
        )}
      </div>
      <div className="flex items-center justify-center gap-1.5">
        <button
          onClick={() => navigate(`/chat?dm=${member.id}&name=${encodeURIComponent(member.full_name)}`)}
          className="p-2 rounded-lg text-bch-teal bg-bch-teal/10 hover:bg-bch-teal/20 transition-colors"
        >
          <ChatCircle className="w-3.5 h-3.5" weight="fill" />
        </button>
        <Link to={`/p/${member.id}`} className="p-2 rounded-lg text-violet-500 bg-violet-500/10 hover:bg-violet-500/20 transition-colors">
          <User className="w-3.5 h-3.5" weight="fill" />
        </Link>
      </div>
    </div>
  );
};
